export const formatNumber = (value: number | null | undefined, decimals = 0): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export const formatKwh = (value: number | null | undefined): string => {
  return `${formatNumber(value, 0)} kWh`
}

export const formatKwp = (value: number | null | undefined): string => {
  return `${formatNumber(value, 1)} kWp`
}

export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—'
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
}

export const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'DEFICIT_CRITICO':
      return 'badge-light-danger'
    case 'LIMITADA':
      return 'badge-light-warning'
    case 'EXPANSAO_RECOMENDADA':
      return 'badge-light-primary'
    case 'OPERACIONAL':
      return 'badge-light-success'
    default:
      return 'badge-light-secondary'
  }
}

export type ExpansionTariffParams = {
  tariffPerKwh?: number
  tariffGroup?: 'A' | 'B' | null
  tariffPonta?: number | null
  tariffForaPonta?: number | null
  tariffSharePonta?: number | null
}

/**
 * Economia / créditos em R$ a partir de kWh × tarifa média.
 */
export const economyFromKwh = (
  kwh: number,
  params: ExpansionTariffParams = {}
): number => {
  if (!Number.isFinite(kwh) || kwh <= 0) return 0
  const tariff = params.tariffPerKwh ?? EXPANSION_MOCK.tariffPerKwh
  return Number((kwh * tariff).toFixed(2))
}

const formatTariffRate = (value: number): string =>
  value.toFixed(4).replace('.', ',')

/**
 * Texto explicativo da tarifa usada na economia/créditos.
 * Grupo A: "tarifa média" + referência ponta / fora ponta.
 * Grupo B: "tarifa".
 */
export const formatExpansionTariffHint = (params: {
  tariffPerKwh: number
  isTariffMock: boolean
  tariffGroup?: 'A' | 'B' | null
  tariffPonta?: number | null
  tariffForaPonta?: number | null
  tariffSharePonta?: number | null
  compact?: boolean
}): string => {
  const source = params.isTariffMock ? 'padrão' : 'da última fatura'
  const group = params.tariffGroup
  const ponta =
    params.tariffPonta != null && params.tariffPonta > 0 ? params.tariffPonta : null
  const fora =
    params.tariffForaPonta != null && params.tariffForaPonta > 0
      ? params.tariffForaPonta
      : null
  const isGrupoA = group === 'A' && (ponta != null || fora != null)
  const tariffLabel = isGrupoA ? 'Tarifa média' : 'Tarifa'

  const ratesHint = [
    ponta != null ? `(ponta R$ ${formatTariffRate(ponta)})` : null,
    fora != null ? `(fora ponta R$ ${formatTariffRate(fora)})` : null,
  ]
    .filter(Boolean)
    .join(' ')

  if (isGrupoA) {
    if (params.compact) {
      return ratesHint || 'Grupo A'
    }
    return ratesHint
      ? `${tariffLabel} ${source} R$ ${formatTariffRate(params.tariffPerKwh)}/kWh ${ratesHint}`
      : `${tariffLabel} ${source} R$ ${formatTariffRate(params.tariffPerKwh)}/kWh (grupo A)`
  }

  if (params.compact) {
    return group ? `Grupo ${group}` : source
  }

  const groupLabel = group ? ` (grupo ${group})` : ''
  return `${tariffLabel} ${source} R$ ${formatTariffRate(params.tariffPerKwh)}/kWh${groupLabel}`
}

/** Constantes mockadas até existirem endpoints/dados reais */
export const EXPANSION_MOCK = {
  peakGenerationKw: 0,
  averageEfficiencyPercent: 0,
  panelWattageWp: 550,
  costPerKwp: 2650,
  tariffPerKwh: 0.74,
  energyInflationPercent: 7.5,
  reinforcedStructure: true,
  laborIncluded: true,
  nextSteps: [
    'Validar capacidade do transformador',
    'Vistoria técnica estrutural',
    'Solicitar orçamento de módulos e inversor',
    'Gerar proposta comercial de expansão',
  ],
}

/** Recalcula excedente e créditos no cliente a partir da meta do slider */
export const simulateFromPlant = (
  generationKwh: number,
  consumptionKwh: number,
  capacityKwp: number,
  targetPercentage: number,
  tariffParams: ExpansionTariffParams | number = EXPANSION_MOCK.tariffPerKwh
) => {
  const tariff =
    typeof tariffParams === 'number'
      ? {tariffPerKwh: tariffParams}
      : tariffParams

  if (consumptionKwh <= 0) {
    return {
      targetGeneration: 0,
      surplusKwh: 0,
      creditsReais: 0,
      suggestedExpansionKwp: 0,
    }
  }

  const targetGeneration = consumptionKwh * (targetPercentage / 100)
  const surplusKwh = Number(Math.max(0, targetGeneration - consumptionKwh).toFixed(0))
  const energyGap = Math.max(0, targetGeneration - generationKwh)

  let suggestedExpansionKwp = 0
  if (capacityKwp > 0 && generationKwh > 0 && energyGap > 0) {
    const yieldPerKwp = generationKwh / capacityKwp
    suggestedExpansionKwp = yieldPerKwp > 0 ? Number((energyGap / yieldPerKwp).toFixed(1)) : 0
  }

  return {
    targetGeneration: Number(targetGeneration.toFixed(0)),
    surplusKwh,
    creditsReais: economyFromKwh(surplusKwh, tariff),
    suggestedExpansionKwp,
  }
}

/**
 * Sugestão simplificada:
 * - potência extra (mesmo cálculo da simulação)
 * - meta ≤ 100%: gap até a meta (acompanha o slider)
 * - meta > 100%: déficit até 100% + excedente da meta
 * - economia / créditos: kWh × tarifa média
 */
export const buildSimpleExpansionSuggestion = (params: {
  generationKwh: number
  consumptionKwh: number
  capacityKwp: number
  targetPercentage: number
  tariffPerKwh?: number
  tariffGroup?: 'A' | 'B' | null
  tariffPonta?: number | null
  tariffForaPonta?: number | null
  tariffSharePonta?: number | null
  months?: number
}) => {
  const tariff: ExpansionTariffParams = {
    tariffPerKwh: params.tariffPerKwh ?? EXPANSION_MOCK.tariffPerKwh,
    tariffGroup: params.tariffGroup,
    tariffPonta: params.tariffPonta,
    tariffForaPonta: params.tariffForaPonta,
    tariffSharePonta: params.tariffSharePonta,
  }
  const target = params.targetPercentage || 100
  const generation = params.generationKwh || 0
  const consumption = params.consumptionKwh || 0
  const capacity = params.capacityKwp || 0

  const targetGeneration = consumption * (target / 100)

  // Abaixo/igual a 100%: gap até a meta do slider. Acima: déficit até 100% + excedente.
  const deficitTo100Kwh =
    target <= 100
      ? Math.max(0, targetGeneration - generation)
      : Math.max(0, consumption - generation)
  const surplusBeyond100Kwh =
    target > 100 ? Math.max(0, targetGeneration - consumption) : 0
  const economyKwh = Number((deficitTo100Kwh + surplusBeyond100Kwh).toFixed(0))
  const economyReais = economyFromKwh(economyKwh, tariff)

  // Anualiza pelo horizonte da análise (ex.: 6 meses → ×2)
  const months = Math.max(params.months || 1, 1)
  const annualEconomyReais = Number(((economyReais * 12) / months).toFixed(2))

  let extraPowerKwp = 0
  const energyGap = Math.max(0, targetGeneration - generation)
  if (capacity > 0 && generation > 0 && energyGap > 0) {
    const yieldPerKwp = generation / capacity
    extraPowerKwp = yieldPerKwp > 0 ? Number((energyGap / yieldPerKwp).toFixed(1)) : 0
  }

  return {
    extraPowerKwp,
    deficitTo100Kwh: Number(deficitTo100Kwh.toFixed(0)),
    surplusBeyond100Kwh: Number(surplusBeyond100Kwh.toFixed(0)),
    economyKwh,
    economyReais,
    annualEconomyReais,
    tariffPerKwh: tariff.tariffPerKwh ?? EXPANSION_MOCK.tariffPerKwh,
  }
}

/**
 * Monta o payload inicial do modal de proposta a partir do cenário de expansão.
 * Sem `id` → modo criação no ModalAddEditV2.
 */
export const buildExpansionProposalDraft = (params: {
  plant: {
    id: string
    name: string
    statusLabel?: string
    address?: string | null
    addressReduce?: string | null
    location?: string | null
    capacityKwp: number
    coveragePercent?: number | null
    deficitKwh?: number
  }
  months: number
  targetPercentage: number
  suggestion: {
    extraPowerKwp: number
    deficitTo100Kwh: number
    surplusBeyond100Kwh: number
    economyKwh: number
    economyReais: number
    annualEconomyReais: number
  }
  simulation?: {
    surplusKwh: number
    creditsReais: number
    suggestedExpansionKwp: number
  } | null
  generationKwh: number
  consumptionKwh: number
  tariffHint?: string
  idUnitBranch?: string | null
  idClient?: string | null
}) => {
  const {plant, suggestion, simulation} = params
  const address =
    plant.address || plant.addressReduce || plant.location || null

  const lines = [
    `Proposta de ampliação gerada a partir da Análise de Ampliação de Ativos.`,
    ``,
    `Usina: ${plant.name};`,
    plant.statusLabel ? `Status: ${plant.statusLabel};` : null,
    `Meta de autossuficiência: ${params.targetPercentage}%;`,
    `Período analisado: ${params.months} mês(es);`,
    `Potência instalada atual: ${formatKwp(plant.capacityKwp)};`,
    suggestion.extraPowerKwp > 0
      ? `Potência extra sugerida: ${formatKwp(suggestion.extraPowerKwp)};`
      : `Potência extra sugerida: —`,
    // simulation && simulation.suggestedExpansionKwp > 0
    //   ? `kWp necessário (simulador): ${formatKwp(simulation.suggestedExpansionKwp)};`
    //   : null,
    `Geração no período: ${formatKwh(params.generationKwh)};`,
    `Consumo no período: ${formatKwh(params.consumptionKwh)};`,
    plant.coveragePercent != null
      ? `Cobertura atual: ${formatNumber(plant.coveragePercent, 1)}%;`
      : null,
    plant.deficitKwh != null && plant.deficitKwh > 0
      ? `Déficit energético: ${formatNumber(plant.deficitKwh, 0)} kWh;`
      : null,
    suggestion.deficitTo100Kwh > 0
      ? `Gap até ${Math.min(params.targetPercentage, 100)}%: ${formatNumber(suggestion.deficitTo100Kwh, 0)} kWh;`
      : null,
    suggestion.surplusBeyond100Kwh > 0
      ? `Excedente da meta: ${formatNumber(suggestion.surplusBeyond100Kwh, 0)} kWh;`
      : null,
    suggestion.economyKwh > 0
      ? `Energia da sugestão: ${formatNumber(suggestion.economyKwh, 0)} kWh;`
      : null,
    suggestion.economyReais > 0
      ? `Economia estimada no período: ${formatCurrency(suggestion.economyReais)};`
      : null,
    suggestion.annualEconomyReais > 0
      ? `Projeção anual: ${formatCurrency(suggestion.annualEconomyReais)};`
      : null,
    simulation && simulation.surplusKwh > 0
      ? `Excedente projetado: ${formatNumber(simulation.surplusKwh, 0)} kWh;`
      : null,
    simulation && simulation.creditsReais > 0
      ? `Créditos de energia: ${formatCurrency(simulation.creditsReais)};`
      : null,
    // params.tariffHint ? `Tarifa: ${params.tariffHint};` : null,
  ].filter((line): line is string => line != null)

  const dateInit = new Date()
  const dateEnd = new Date(dateInit)
  dateEnd.setMonth(dateEnd.getMonth() + Math.max(params.months || 1, 1))

  return {
    title: `Ampliação — ${plant.name}`,
    description: lines.join('\n'),
    address,
    dateInit,
    dateEnd,
    idPlant: plant.id,
    idUnitBranch: params.idUnitBranch || null,
    idClient: params.idClient || null,
    idsCollaborator: [] as string[],
    idPhaseFunnel: null as string | null,
  }
}

/**
 * Sugestão de expansão (versão completa — mantida comentada na UI):
 * - potência extra / painéis: derivados do kWp sugerido (calculável)
 * - investimento / ROI / economia anual: estimativas mockadas (salvo tarifa, quando informada)
 */
export const buildExpansionSuggestion = (
  suggestedExpansionKwp: number,
  months: number,
  tariffPerKwh = EXPANSION_MOCK.tariffPerKwh
) => {
  const extraPowerKwp = suggestedExpansionKwp > 0 ? suggestedExpansionKwp : 0
  const newPanels =
    extraPowerKwp > 0
      ? Math.ceil((extraPowerKwp * 1000) / EXPANSION_MOCK.panelWattageWp)
      : 0

  const estimatedInvestment =
    extraPowerKwp > 0 ? Number((extraPowerKwp * EXPANSION_MOCK.costPerKwp).toFixed(0)) : 0

  // Economia anual: assume yield médio ~1200 kWh/kWp.ano e tarifa (fatura ou mock)
  const annualExtraKwh = extraPowerKwp * 1200
  const annualSavingsBase = annualExtraKwh * tariffPerKwh
  const annualSavingsWithInflation = Number(
    (annualSavingsBase * (1 + EXPANSION_MOCK.energyInflationPercent / 100)).toFixed(0)
  )

  const paybackYears =
    estimatedInvestment > 0 && annualSavingsWithInflation > 0
      ? Number((estimatedInvestment / annualSavingsWithInflation).toFixed(1))
      : 0

  return {
    extraPowerKwp,
    newPanels,
    panelWattageWp: EXPANSION_MOCK.panelWattageWp,
    estimatedInvestment,
    paybackYears,
    annualSavings: annualSavingsWithInflation,
    energyInflationPercent: EXPANSION_MOCK.energyInflationPercent,
    reinforcedStructure: EXPANSION_MOCK.reinforcedStructure,
    laborIncluded: EXPANSION_MOCK.laborIncluded,
    nextSteps: EXPANSION_MOCK.nextSteps,
    sources: {
      extraPower: 'calculated' as const,
      newPanels: 'calculated' as const,
      estimatedInvestment: 'mock' as const,
      paybackYears: 'mock' as const,
      annualSavings: 'mock' as const,
      peakGeneration: 'mock' as const,
      averageEfficiency: 'mock' as const,
    },
    months,
  }
}
